import {InteractiveAuthenticationRequiredMessage} from "../shared/errorMessages";
import {ApplicationInsightsTelemetry} from "../utils/telemetry/applicationInsightsTelemetry";

const credentials: RequestCredentials = "include";
const defaultHeaders = {"Content-Type": "application/json"};

export interface IBackendCaller {
  callApi: (
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    shouldThrowRawError?: boolean,
    abortController?: AbortController
  ) => Promise<any>;
  getFile: (
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    abortController?: AbortController
  ) => Promise<any>;

  downloadFile: (
    filename: string,
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    abortController?: AbortController
  ) => Promise<void>;
}

export class BackendCaller implements IBackendCaller {
  callApi = async (
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    shouldThrowRawError?: boolean,
    abortController?: AbortController
  ): Promise<any> => {
    return await fetch(url, {
      method: method,
      credentials: credentials,
      headers: headers ?? defaultHeaders,
      body: body,
      signal: abortController?.signal
    })
      .then(async (response: Response) => {
        if (response.ok) {
          return await response.json();
        }

        if (shouldThrowRawError) {
          throw await response.json();
        }

        if (
          response.status === 401 ||
          (await response.text()).includes("MsalUiRequiredException")
        ) {
          throw Error(InteractiveAuthenticationRequiredMessage);
        }
        throw Error(response.statusText);
      })
      .catch((error: Error) => {
        if (abortController?.signal.aborted) return;

        ApplicationInsightsTelemetry.getInstance().applicationInsights?.trackException(
          {
            error: error
          }
        );
        throw error;
      });
  };

  getFile = async (
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    abortController?: AbortController
  ): Promise<any> => {
    return await fetch(url, {
      method: method,
      credentials: credentials,
      headers: headers ?? defaultHeaders,
      body: body,
      signal: abortController?.signal
    })
      .then(async (response: Response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        } else {
          return response.blob();
        }
      })
      .catch((error: Error) => {
        if (abortController?.signal.aborted) return;

        ApplicationInsightsTelemetry.getInstance().applicationInsights?.trackException(
          {
            error: error
          }
        );
        throw error;
      });
  };

  downloadFile = async (
    filename: string,
    method: string,
    url: string,
    body?: any | null,
    headers?: any | null,
    abortController?: AbortController
  ) => {
    return await fetch(url, {
      method: method,
      credentials: credentials,
      headers: headers ?? defaultHeaders,
      body: body,
      signal: abortController?.signal
    })
      .then(async (response: Response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        } else {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          // Create a link element, click it, and remove it
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.append(link);
          link.click();
          link.parentNode?.removeChild(link);
        }
      })
      .catch((error: Error) => {
        if (abortController?.signal.aborted) return;

        ApplicationInsightsTelemetry.getInstance().applicationInsights?.trackException(
          {
            error: error
          }
        );
        throw error;
      });
  };
}
